import React from 'react';
import { Gamepad2, ChevronLeft, ChevronRight, Box, Sparkles, Crown, Zap, Swords } from 'lucide-react';
import { games, cubeImage } from '../mock';
import { useLang } from '../LangContext';

// Fallback CSS art for games without images
const FallbackArt = ({ name }) => {
  if (name === 'ROULETTE ORACLE') {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#3a0a0a] to-[#0b1224]">
        <div className="w-24 h-24 rounded-full border-4 border-amber-500 bg-gradient-to-br from-red-600 via-slate-900 to-red-600 shadow-[0_0_20px_rgba(251,191,36,0.4)] flex items-center justify-center">
          <div className="w-8 h-8 rounded-full bg-amber-400" />
        </div>
      </div>
    );
  }
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#0a1b3a] to-[#0b1224]">
      <Crown className="w-16 h-16 text-amber-400 drop-shadow-[0_0_12px_rgba(251,191,36,0.6)]" />
    </div>
  );
};

const Games = ({ onPlay }) => {
  const { t } = useLang();
  const handlePlay = (game) => {
    if (game.status === 'COMING SOON') return;
    if (game.name === 'DOMINO' && typeof onPlay === 'function') {
      onPlay('domino');
    }
    if (game.name === 'TIC-TAC-TOE' && typeof onPlay === 'function') {
      onPlay('tictactoe');
    }
    if (game.name === 'LUDO' && typeof onPlay === 'function') {
      onPlay('ludo');
    }
  };
  return (
    <section id="games" className="relative z-10 max-w-[1400px] mx-auto px-6 mt-12">
      <div className="section-title">
        <div className="line" />
        <div className="pill"><Gamepad2 className="w-4 h-4 text-blue-600" /> {t.games.title}</div>
        <div className="line" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        {/* Games carousel */}
        <div className="relative">
          <button className="absolute -left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center hover:bg-blue-50">
            <ChevronLeft className="w-4 h-4 text-slate-700" />
          </button>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {games.map((g) => (
              <div key={g.id} className="game-card">
                <div className="aspect-[3/4] relative">
                  {g.image ? (
                    <img src={g.image} alt={g.name} className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <FallbackArt name={g.name} />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b1224]/95 via-[#0b1224]/20 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-2.5">
                    <div className="text-white font-black text-[12px] text-center tracking-wide drop-shadow-lg">{g.name}</div>
                    <button
                      onClick={() => handlePlay(g)}
                      data-testid={g.name === 'DOMINO' ? 'play-domino-btn' : g.name === 'TIC-TAC-TOE' ? 'play-tictactoe-btn' : g.name === 'LUDO' ? 'play-ludo-btn' : `play-${g.id}-btn`}
                      className={`mt-2 play-btn ${g.status === 'COMING SOON' ? 'soon' : ''}`}
                    >
                      {g.status === 'COMING SOON' ? t.games.comingSoon : t.games.playNow}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button className="absolute -right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center hover:bg-blue-50">
            <ChevronRight className="w-4 h-4 text-slate-700" />
          </button>
        </div>

        {/* Cube Evolution */}
        <div className="cube-card star-bg">
          <div className="flex items-start justify-between">
            <h3 className="text-2xl font-black tracking-tight">{t.cube.title}</h3>
            <span className="gold-pill flex items-center gap-1"><Sparkles className="w-3 h-3" />{t.cube.new}</span>
          </div>
          <div className="relative my-5 flex items-center justify-center">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-48 rounded-full bg-blue-500/20 blur-2xl" />
            </div>
            <img src={cubeImage} alt="Evolution Cube" className="relative w-44 h-44 object-contain drop-shadow-[0_10px_30px_rgba(59,130,246,0.5)]" />
            <Zap className="absolute -top-2 right-6 w-5 h-5 text-amber-300 pulse-anim" />
          </div>
          <p className="text-[13px] text-blue-100 leading-relaxed text-center px-2">{t.cube.desc}</p>
          <button className="mt-5 w-full bg-gradient-to-r from-blue-600 to-blue-400 hover:from-blue-500 hover:to-blue-300 transition rounded-full py-3 font-extrabold text-white text-[13px] flex items-center justify-center gap-2 shadow-[0_10px_24px_rgba(37,99,235,0.45)]">
            <Box className="w-4 h-4" />
            {t.cube.button}
          </button>
        </div>
      </div>
    </section>
  );
};

export default Games;
